import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, MapPin, Navigation, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    trackPageView('/locations', 'Our Locations');
    
    fetch(`${BACKEND_URL}/api/locations`)
      .then(res => res.json())
      .then(data => setLocations(data))
      .catch(err => console.error('Error fetching locations:', err));
  }, []);

  const getGoogleMapsUrl = (address) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  return (
    <div className="min-h-screen bg-void" data-testid="locations-page">
      <SEOHead
        title="Our Locations"
        description="Visit Avi Cement manufacturing plants in Gujarat (Ranasan GIDC) and Rajasthan (Ghatol), or our head office in Ahmedabad. Find directions and contact details."
        path="/locations"
      />
      {/* Hero Section */}
      <section className="relative py-12 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <motion.div
          className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 60%)' }}
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <span className="text-coral font-mono text-sm mb-4 block">LOCATIONS</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Find us<br />
              <span className="text-gradient">near you</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Strategically positioned across Gujarat and Rajasthan to serve you better.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Location Cards */}
      <section ref={ref} className="py-12 md:py-32 bg-surface">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {locations.map((location, index) => (
              <motion.div
                key={location.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.15 }}
                data-testid={`location-card-${location.id}`}
                className="bg-void border border-white/5 rounded-2xl overflow-hidden hover:border-white/20 transition-colors group"
              >
                {/* Header */}
                <div className="p-6 border-b border-white/5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                      {location.type}
                    </span>
                    <div className="w-10 h-10 bg-coral/20 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-coral" />
                    </div>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white">
                    {location.name}
                  </h3>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Navigation className="w-4 h-4 text-gray-500 mt-1 flex-shrink-0" />
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {location.address}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <a href={`tel:${location.phone}`} className="text-gray-400 text-sm hover:text-coral transition-colors">
                      {location.phone}
                    </a>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <a href={`mailto:${location.email}`} className="text-gray-400 text-sm hover:text-coral transition-colors">
                      {location.email}
                    </a>
                  </div>
                </div>

                {/* CTA */}
                <div className="p-6 pt-0">
                  <a
                    href={getGoogleMapsUrl(location.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-surface-light hover:bg-coral text-white font-display text-sm py-3 rounded-xl transition-colors group"
                  >
                    Get Directions
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-void">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="font-display text-2xl font-bold text-white text-center mb-8">
            Our Coverage Area
          </h2>
          
          <div className="aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden border border-white/5">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3671.524076193456!2d72.55740707529426!3d23.046900079152847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e8482f5f7f9e7%3A0x8a43d3d75e7c94e3!2sJay%20Tower%2C%20Ankur%20Rd%2C%20Prahalad%20Society%2C%20Rang%20Jyot%20Society%2C%20Naranpura%2C%20Ahmedabad%2C%20Gujarat%20380013!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Avi Cement Locations"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-surface border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                Need to reach us?
              </h3>
              <p className="text-gray-400">Our team is ready to assist with any inquiries.</p>
            </div>
            <Link
              to="/contact"
              className="group flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-6 py-3 rounded-full transition-all"
            >
              Contact Us <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationsPage;
